/**
 * accountDeletionApi (1.34.0)
 *
 * Запрос на удаление аккаунта пользователя и отмена такого запроса.
 *
 * Архитектура:
 *  • request_account_deletion RPC — создаёт запись в
 *    user_account_deletion_requests с scheduled_anonymization_at =
 *    now() + 30 дней. Если активный запрос уже есть — возвращает его.
 *  • cancel_account_deletion RPC — ставит cancelled_at на активном
 *    запросе.
 *  • Через 30 дней cron вызывает anonymize_user_account (service_role).
 *
 * Пока запрос активен (не cancelled, не completed) — пользователь
 * заходит в приложение, но видит только экран отмены/выхода.
 */

import { isSupabaseConfigured, requireSupabase } from '../auth/supabaseClient';

export interface AccountDeletionRequest {
  id: string;
  user_id: string;
  requested_at: string;
  scheduled_anonymization_at: string;
  cancelled_at: string | null;
  completed_at: string | null;
  user_agent: string | null;
}

/**
 * Возвращает активный запрос на удаление текущего пользователя,
 * если он есть. Null если активного запроса нет.
 */
export async function fetchActiveDeletionRequest(
  userId: string,
): Promise<AccountDeletionRequest | null> {
  if (!isSupabaseConfigured) return null;
  const supabase = requireSupabase();

  const { data, error } = await supabase
    .from('user_account_deletion_requests')
    .select('*')
    .eq('user_id', userId)
    .is('cancelled_at', null)
    .is('completed_at', null)
    .maybeSingle();

  if (error) throw new Error(`fetchActiveDeletionRequest: ${error.message}`);
  return (data as AccountDeletionRequest) ?? null;
}

/**
 * Возвращает Map { user_id → scheduled_anonymization_at } для активных
 * запросов на удаление указанных пользователей. Если у юзера нет
 * запроса, его в Map не будет. Используется в StaffList (1.34.0) чтобы
 * пометить уходящих сотрудников бейджем «удаляется».
 *
 * RLS гарантирует, что админ компании видит запросы только своих
 * сотрудников (политика uadr_select_company_admin в миграции).
 */
export async function fetchActiveDeletionRequestsForUsers(
  userIds: string[],
): Promise<Map<string, string>> {
  if (!isSupabaseConfigured || userIds.length === 0) return new Map();
  const supabase = requireSupabase();

  const { data, error } = await supabase
    .from('user_account_deletion_requests')
    .select('user_id, scheduled_anonymization_at')
    .in('user_id', userIds)
    .is('cancelled_at', null)
    .is('completed_at', null);

  if (error) throw new Error(`fetchActiveDeletionRequestsForUsers: ${error.message}`);

  const map = new Map<string, string>();
  for (const row of (data ?? []) as Array<{ user_id: string; scheduled_anonymization_at: string }>) {
    map.set(row.user_id, row.scheduled_anonymization_at);
  }
  return map;
}

/**
 * Создаёт запрос на удаление аккаунта (или возвращает существующий).
 * Через 30 дней cron вызовет anonymize_user_account.
 */
export async function requestAccountDeletion(): Promise<AccountDeletionRequest> {
  const supabase = requireSupabase();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;

  const { data, error } = await supabase.rpc('request_account_deletion', {
    p_user_agent: ua,
  });

  if (error) throw new Error(`requestAccountDeletion: ${error.message}`);
  return data as AccountDeletionRequest;
}

/**
 * Отменяет активный запрос на удаление.
 * Возвращает true если запрос был и его отменили, false если активного не было.
 */
export async function cancelAccountDeletion(): Promise<boolean> {
  const supabase = requireSupabase();

  const { data, error } = await supabase.rpc('cancel_account_deletion');
  if (error) throw new Error(`cancelAccountDeletion: ${error.message}`);
  return Boolean(data);
}
